import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { subscriptionKeys } from '@/api/keys/subscriptionKeys';
import { refreshBillingState, type BillingRefreshOutcome } from '@/lib/billingRefresh';
import type {
  Subscription,
  SubscriptionPlan,
  CreateCheckoutSessionRequest,
  CheckoutSessionResponse,
  PortalSessionResponse,
  FeatureCheckResponse,
  FeatureName,
} from '@/types/subscription';

// Re-export types for convenience
export type {
  Subscription,
  SubscriptionPlan,
  Entitlements,
  SubscriptionTier,
  SubscriptionStatus,
  FeatureName,
  UpgradePromptContext,
} from '@/types/subscription';

export {
  getTierDisplayName,
  getUpgradePromptCta,
  isPremiumTier,
  UPGRADE_PROMPT_COPY,
} from '@/types/subscription';

/**
 * Hook to get the current user's subscription and entitlements.
 */
export function useGetSubscription(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: () => apiFetch<Subscription>('/subscriptions/'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled,
  });
}

/**
 * Hook to get available subscription plans.
 */
export function useGetSubscriptionPlans(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: subscriptionKeys.plans(),
    queryFn: () => apiFetch<SubscriptionPlan[]>('/subscriptions/plans/'),
    staleTime: 60 * 60 * 1000, // 1 hour - plans rarely change
    enabled: options?.enabled,
  });
}

/**
 * Hook to check feature access.
 */
export function useCheckFeature(
  feature: FeatureName,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: subscriptionKeys.feature(feature),
    queryFn: () =>
      apiFetch<FeatureCheckResponse>(`/subscriptions/check-feature/?feature=${feature}`),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled,
  });
}

/**
 * Hook to create a Stripe Checkout session.
 */
export function useCreateCheckoutSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCheckoutSessionRequest) =>
      apiFetch<CheckoutSessionResponse>('/subscriptions/checkout/', {
        method: 'POST',
        body: {
          priceId: data.priceId,
          idempotencyToken: data.idempotencyToken,
          successUrl: data.successUrl,
          cancelUrl: data.cancelUrl,
          coupon: data.coupon,
        },
      }),
    onSuccess: () => {
      // Invalidate subscription data after checkout
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

/**
 * Hook to create a Stripe Customer Portal session.
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: (returnUrl?: string) =>
      apiFetch<PortalSessionResponse>('/subscriptions/portal/', {
        method: 'POST',
        body: JSON.stringify({
          return_url: returnUrl,
        }),
      }),
  });
}

/**
 * The post-billing convergence point (D51f Phase 5, FR-P15): returns a stable
 * function that refreshes the entitlements, the current issue and the archive
 * (reset to page 1) after checkout success, a customer-portal return, or a
 * native foreground return from Stripe, and then polls the entitlements a
 * bounded number of times to absorb the Stripe-webhook race. See
 * `@/lib/billingRefresh` for the order, the poll and its early stop.
 *
 * The returned function resolves with how the run ended (callers may ignore
 * it). The run is NOT cancelled when the calling component unmounts: it only
 * touches the app-wide `QueryClient`, so a member who taps Back from
 * Subscription settings mid-poll must still get the webhook race absorbed.
 * A newer run for the same client supersedes an older one (a native return
 * that fires the foreground listener and a deep link polls once, not twice),
 * and `clearSession` cancels it on logout (`cancelBillingRefresh`).
 *
 * The function is memoised on purpose: it sits in effect dependency arrays
 * (`SubscriptionSettings`), and a new identity per render would restart the
 * run.
 */
export function useInvalidateSubscription(): () => Promise<BillingRefreshOutcome> {
  const queryClient = useQueryClient();
  return useCallback(() => refreshBillingState(queryClient).done, [queryClient]);
}
